// ./src/store/slices/invitationSlice.ts

import { createSlice, PayloadAction, Action, createAsyncThunk, current } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { createSelector } from 'reselect';
import { Contact, Relative, RelativeWithInviteStatus, updateContact } from './contactSlice';
import { Dayjs } from 'dayjs';
import { isValidFlowPage } from '../../utils/urls';

import designReducer, { DesignState, selectDesignDataForChanges as selectDesignSliceDataForChanges } from './designSlice';

export interface NewGuestContact extends Guest {
  authorId?: number;
  tags?: string[];
  relatives?: (Relative | RelativeWithInviteStatus)[];  // Can be base Relative or extended with isInvited
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

// Guest and Invitation-related interfaces
export interface Guest {
  guestRelayId?: string;
  guestId: number;
  contactId: number;
  invitationProjectId: number;
  rsvpStatus: string;
  comments: string;
  sentStatus: string;
  savedToDb?: boolean;
  relatives?: Relative[];
  guestStatus?: 'publish' | 'draft'; // New field to track guest status
  deletionReason?: string;
  deletedAt?: string; 
  deletedBy?: number; 
  deletionEmailSent?: boolean;
  openedStatus?: boolean; 
  openedCount?: number; 
  firstOpenedAt?: string;
  lastOpenedAt?: string; 
  reminderSentAt?: string;
  envelopeFrontBitmap?: string;
  needsEnvelopeRegeneration?: boolean; // Flag to track if guest's envelope needs regeneration (e.g., relatives changed)
}

export interface ProjectGuests {
  guestList: Guest[];
  newGuest: Guest;
}

export interface CustomWish {
    id?: string; // Unique ID for list key and deletion
    name: string;
    url?: string;
}

export interface CustomWishlist {
    listName: string;
    wishes: CustomWish[];
}

export interface WishlistItem {
    name: string;
    url: string;
}

export interface ProjectDetailsState {
  projectTitle?: string;
  eventOwner?: string;
  extraEventOwner?: string;
  eventDate?: Dayjs | string | null;
  eventTime?: Dayjs | string | null;
  eventEndDate?: Dayjs | string | null;
  eventEndTime?: Dayjs | string | null;
  venueName?: string;
  address?: string;
  zipcode?: string;
  city?: string;
  eventDescription?: string;
  locationDescription?: string;
  rsvpDueDate?: Dayjs | string | null;
  reminderEnabled?: boolean;
  reminderSentAt?: string | null;
  signatureGreeting?: string;
  wishlistEnabled?: boolean;
  wishlistType?: 'link' | 'custom';
  wishlist?: WishlistItem[]; // Array of named wishlists for link type
  customWishes?: Array<{
    listName: string;
    wishes: CustomWish[];
  }>;
}

// Represents the state of a single invitation project being created or edited
export interface EditableInvitationProjectState {
  userId?: number;
  projectId?: number;
  projectPostTitle?: string;
  status: string;
  projectDetails: ProjectDetailsState;
  projectGuests: ProjectGuests;
  design: DesignState;
  lastVisitedPage?: string | null;
  error?: string | null;
  completedSteps: string[];
  envelopeFrontInvalidated?: boolean; // 🎯 CRITICAL: Add envelope flag to project state
}

// Type used for saving/comparing changes - adjusted to reflect nested design
export interface CompleteProjectDataType {
  userId?: number;
  projectId?: number;
  status: string;
  projectDetails: ProjectDetailsState & { projectTitle: string };
  projectGuests: ProjectGuests;
  design: DesignState;
  error?: string | null;
  completedSteps: string[];
  lastVisitedPage?: string | null;
  envelopeFrontInvalidated?: boolean; // 🎯 CRITICAL: Include envelope flag for change detection
}

// Represents the overall state managed by this slice
interface InvitationState {
  invitationProjects: any[]; // TODO: Define a proper summary type if needed for lists
  invitationProjectState: EditableInvitationProjectState; // Current project being edited
  loading: boolean;
  error?: string | null;
  lastSavedData: CompleteProjectDataType | undefined; // Use updated type
  isSaveEnabled: boolean;
  mobileTextEditPanel: {
    open: boolean;
    activeTextBlockId: string | null;
  };
  mobileFigureEditPanel: {
    open: boolean;
    activeFigureBlockId: string | null;
  };
}

// Initial state
const initialState: InvitationState = {
  invitationProjects: [],
  invitationProjectState: {
    userId: undefined,
    projectId: undefined,
    projectPostTitle: '',
    status: 'draft',
    projectDetails: {
      projectTitle: '',
      eventOwner: '',
      extraEventOwner: '',
      eventDate: null,
      eventTime: null,
      eventEndDate: null,
      eventEndTime: null,
      venueName: '',
      address: '',
      zipcode: '',
      city: '',
      eventDescription: '',
      locationDescription: '',
      signatureGreeting: 'De bedste hilsner',
      rsvpDueDate: null,
      reminderEnabled: false,
      reminderSentAt: null,
      wishlistEnabled: false,
      wishlistType: 'link',
      wishlist: [],
      customWishes: [], // Array of blocks with listName and wishes
    },
    projectGuests: {
      guestList: [],
      newGuest: {
        guestId: 0,
        contactId: 0,
        invitationProjectId: 0,
        rsvpStatus: '',
        comments: '',
        sentStatus: 'Not Sent',
        tags: [],
        guestStatus: 'publish', // Default to publish
        deletionReason: undefined,
        deletedAt: undefined,
        deletedBy: undefined,
        deletionEmailSent: undefined,
        openedStatus: false, // Default to not opened
        openedCount: 0, // Default to 0 opens
        firstOpenedAt: undefined,
        lastOpenedAt: undefined,
        reminderSentAt: undefined, // Default to no reminder sent
      } as NewGuestContact,
    },
    design: designReducer(undefined, { type: '@@INIT' }),
    lastVisitedPage: null,
    error: null,
    completedSteps: [],
    envelopeFrontInvalidated: false, // 🎯 CRITICAL: Initialize envelope flag in project state
  },
  loading: false,
  error: null,
  lastSavedData: undefined,
  isSaveEnabled: false,
  mobileTextEditPanel: {
    open: false,
    activeTextBlockId: null,
  },
  mobileFigureEditPanel: {
    open: false,
    activeFigureBlockId: null,
  },
  // 🔧 REMOVED: completedSteps (only exists in invitationProjectState - single source of truth)
};

// Selector to get complete project data from invitationProjectState
export const completeProjectData = createSelector(
  (state: RootState) => state.invitation.invitationProjectState,
  (invitationProjectState) => {
    // Construct the object based on CompleteProjectDataType using only invitationProjectState
    const result: CompleteProjectDataType = {
      userId: invitationProjectState.userId,
      projectId: invitationProjectState.projectId,
      status: invitationProjectState.status,
      projectDetails: {
        ...invitationProjectState.projectDetails,
        projectTitle: invitationProjectState.projectDetails?.projectTitle || '',
      },
      projectGuests: invitationProjectState.projectGuests,
      design: invitationProjectState.design,
      completedSteps: invitationProjectState.completedSteps,
      error: invitationProjectState.error,
      lastVisitedPage: invitationProjectState.lastVisitedPage,
      envelopeFrontInvalidated: invitationProjectState.envelopeFrontInvalidated,
    };
    return result;
  }
);

// Selector to get only the parts needed for change detection
const selectCurrentDataForChanges = createSelector(
  (state: RootState) => state.invitation.invitationProjectState,
  (invitationProjectState) => ({
    projectDetails: invitationProjectState.projectDetails,
    // ❌ REMOVED: projectGuests - guests are saved directly, no need for change detection
    design: invitationProjectState.design, // Include the nested design state
    completedSteps: invitationProjectState.completedSteps,
    status: invitationProjectState.status, // Include status if relevant for change detection
    lastVisitedPage: invitationProjectState.lastVisitedPage, // Add lastVisitedPage to change detection
    envelopeFrontInvalidated: invitationProjectState.envelopeFrontInvalidated, // Include envelope flag for change detection
  })
);

// --- MODIFIED deepCompare Implementation with Optimized Logging --- 
function deepCompare(newData: any, oldData: any, path: string = 'root'): any {
  // Skip comparison for navigation/progress fields that shouldn't trigger save button
  // These fields change during normal navigation but shouldn't enable the save button
  if (path === 'completedSteps' || path === 'lastVisitedPage') {
    return undefined; // Return undefined to indicate no change detected
  }
  
  // Primitive type comparison (base case)
  if (typeof newData !== 'object' || newData === null || typeof oldData !== 'object' || oldData === null) {
    if (newData !== oldData) {
        return newData;
    }
    return undefined;
  }
  
  // Array handling with special case for textblocks
  if (Array.isArray(newData)) {
    if (!Array.isArray(oldData) || newData.length !== oldData.length) {
      return newData; // Array structure changed
    }
    
    // Special handling for textblocks, imageBlocks, and figureBlocks arrays
    if (path === 'root.design.designTextblocks' || path === 'root.design.imageBlocks' || path === 'root.design.figureBlocks' ||
        path === 'design.designTextblocks' || path === 'design.imageBlocks' || path === 'design.figureBlocks') {
      
      let arrayChanged = false;
      
      // Compare each item individually without the JSON.stringify shortcut
      for (let i = 0; i < newData.length; i++) {
        const itemPath = `${path}[${i}]`;
        const currItem = newData[i];
        const prevItem = oldData[i];
        
        // For textblocks/imageBlocks/figureBlocks, first check if IDs match to avoid false comparisons
        if (currItem.textblockId && prevItem?.textblockId && currItem.textblockId === prevItem.textblockId) {
          const diff = deepCompare(currItem, prevItem, itemPath);
          if (diff !== undefined) {
            arrayChanged = true;
            break;
          }
        } else if (currItem.imageBlockId && prevItem?.imageBlockId && currItem.imageBlockId === prevItem.imageBlockId) {
          const diff = deepCompare(currItem, prevItem, itemPath);
          if (diff !== undefined) {
            arrayChanged = true;
            break;
          }
        } else if (currItem.figureBlockId && prevItem?.figureBlockId && currItem.figureBlockId === prevItem.figureBlockId) {
          const diff = deepCompare(currItem, prevItem, itemPath);
          if (diff !== undefined) {
            arrayChanged = true;
            break;
          }
        } else {
          arrayChanged = true;
          break;
        }
      }
      
      if (arrayChanged) {
        return newData;
      }
      return undefined;
    }
    
    // Standard array comparison for non-textblock arrays (preserve existing logic)
    let arrayChanged = false;
    
    // Retain the JSON.stringify optimization for non-design arrays for performance
    if (JSON.stringify(newData) !== JSON.stringify(oldData)) { 
      for (let i = 0; i < newData.length; i++) {
        const itemPath = `${path}[${i}]`;
        // Recursive call for array items
        if (deepCompare(newData[i], oldData[i], itemPath) !== undefined) {
          arrayChanged = true;
          break; // Found a difference, no need to check further
        }
      }
    }
    
    if (arrayChanged) {
      return newData;
    }
    return undefined; // No changes in array
  }

  // Handle Objects - Keep existing logic
  const changedObject: Record<string, any> = {};
  let hasChanges = false;
  const newKeys = Object.keys(newData);
  const oldKeys = Object.keys(oldData || {});
  const allKeys = new Set([...newKeys, ...oldKeys]);

  for (const key of allKeys) {
    // Targeted Key Exclusion - preserve existing exclusions
    if (key === 'viewMode' || key === 'lastTextView') {
      continue; 
    }
    
    // Skip other internal/transient keys
    if (
      ['savedToDb', 'isModified', 'textBlocksLoaded', 'activeEditor',
       'error', 'searchTerm', 'designUsageCounts', '__typename', 'loading', 
       'lastSavedData', 'removedTextblockIds', 'removedImageblockIds', 'removedFigureblockIds',
       'livePreviewUrl', 'livePreviewShowCard' // Frontend-only cache fields, not persisted to DB
      ].includes(key) 
    ) {
      continue; 
    }

    // Comparison Logic 
    const newValue = newData ? newData[key] : undefined;
    const oldValue = oldData ? oldData[key] : undefined;
    
    // Recursive call for non-excluded keys
    const change = deepCompare(newValue, oldValue, `${path}.${key}`);

    if (change !== undefined) {
      changedObject[key] = newValue; 
      hasChanges = true;
    } 
  }
  
  // Return undefined if changedObject is empty (no actual changes found)
  if (hasChanges && Object.keys(changedObject).length === 0) {
    return undefined;
  }

  return hasChanges ? changedObject : undefined;
}


// --- END deepCompare Implementation ---

// Helper function to check if an object is non-null and has keys
const hasActualChanges = (obj: any): boolean => {
    return obj && typeof obj === 'object' && Object.keys(obj).length > 0;
};

// --- MODIFIED selectProjectChanges --- 
export const selectProjectChanges = createSelector(
    (state: RootState) => state.invitation.lastSavedData,
    selectCurrentDataForChanges,
    (lastSavedData, currentData) => {
      const timestamp = new Date().toLocaleTimeString();

      if (!lastSavedData) {
        return currentData; 
      }
  
      // --- Run comparisons --- 
      const designChangesResult = deepCompare(currentData.design, lastSavedData.design, 'design');
      const projectDetailsChanges = deepCompare(currentData.projectDetails, lastSavedData.projectDetails, 'projectDetails');
      // ❌ REMOVED: projectGuestsChanges - guests are saved directly, no need for change detection
      const completedStepsChanged = deepCompare(currentData.completedSteps, lastSavedData.completedSteps, 'completedSteps');
      const envelopeFlagChanged = deepCompare(currentData.envelopeFrontInvalidated, lastSavedData.envelopeFrontInvalidated, 'envelopeFrontInvalidated');
      
      // --- Explicit Status Comparison Logging ---
      const statusChanged = currentData.status !== lastSavedData.status;

      const removedTextIds = currentData.design?.removedTextblockIds || [];
      const removedImageIds = currentData.design?.removedImageblockIds || [];
      const removedFigureIds = currentData.design?.removedFigureblockIds || [];
      
      // --- Use helper to check if results ACTUALLY contain changes --- 
      const hasDesignChanges = hasActualChanges(designChangesResult) || removedTextIds.length > 0 || removedImageIds.length > 0 || removedFigureIds.length > 0;
      const hasDetailsChanges = hasActualChanges(projectDetailsChanges);
      // ❌ REMOVED: hasGuestsChanges - guests are saved directly, no need for change detection
      const hasStepsChanges = hasActualChanges(completedStepsChanged);
      const hasEnvelopeFlagChanges = hasActualChanges(envelopeFlagChanged);

  
      const changes: Partial<CompleteProjectDataType> = {};
      let needsSave = false;
  
      // --- Construct changes object ONLY if changes were definitively found --- 
      if (hasDesignChanges) {
          const baseDesignChanges = designChangesResult ? designChangesResult as Partial<DesignState> : {};
          
          // Initial construction (handles cases where fields other than removed IDs changed)
          changes.design = { 
              ...baseDesignChanges,
              designId: baseDesignChanges.designId !== undefined ? baseDesignChanges.designId : currentData.design?.designId ?? null,
              ...(removedTextIds.length > 0 && { removedTextblockIds: removedTextIds }),
              ...(removedImageIds.length > 0 && { removedImageblockIds: removedImageIds }),
              ...(removedFigureIds.length > 0 && { removedFigureblockIds: removedFigureIds })
           } as DesignState; 

          // CORRECTED BLOCK: If *only* removed IDs changed, 
          // ensure essential fields are included without duplication.
          if (!designChangesResult && (removedTextIds.length > 0 || removedImageIds.length > 0 || removedFigureIds.length > 0)) {
              changes.design = { 
                   designId: currentData.design?.designId ?? null, 
                   designTitle: currentData.design?.designTitle ?? '',
                   cardBackgroundImage: currentData.design?.cardBackgroundImage ?? '',
                   removedTextblockIds: changes.design.removedTextblockIds, 
                   removedImageblockIds: changes.design.removedImageblockIds,
                   removedFigureblockIds: changes.design.removedFigureblockIds,
                } as DesignState;
          }
          needsSave = true;
      }
      if (hasDetailsChanges) {
          changes.projectDetails = projectDetailsChanges as ProjectDetailsState & { projectTitle: string };
          needsSave = true;
      }
      // ❌ REMOVED: hasGuestsChanges logic - guests are saved directly, no need for change detection
      if (hasStepsChanges) {
        changes.completedSteps = completedStepsChanged as string[]; // Assuming deepCompare returned the new array
        needsSave = true;
      }
      if (statusChanged) {
        changes.status = currentData.status; // Add status to changes if it changed
        needsSave = true;
      }
      if (hasEnvelopeFlagChanges) {
        changes.envelopeFrontInvalidated = currentData.envelopeFrontInvalidated; // Add envelope flag to changes if it changed
        needsSave = true;
      }
  
      const finalResult = needsSave ? changes : undefined;
      return finalResult;
    }
  );

// Simple selector for envelope front invalidated flag
export const selectEnvelopeFrontInvalidated = (state: RootState): boolean => {
  return state.invitation.invitationProjectState.envelopeFrontInvalidated || false;
};

function isActionWithPayload(action: Action<any>): action is Action<any> & { payload: any } {
  return 'payload' in action;
}

export const updateContactAndGuest = createAsyncThunk<void, { tempId: number; updatedContact: Contact; updatedGuest: Guest }, { state: RootState }>('invitation/updateContactAndGuest', async ({ tempId, updatedContact, updatedGuest }, { dispatch, getState }) => {
  const state = getState();

  // Validate tempId exists in the contact slice
  const existingContact = state.contacts.contacts.find((contact: Contact) => contact.contactId === tempId);
  if (!existingContact) {
    return;
  }

  // Validate tempId exists in the guest slice
  const existingGuest = state.invitation.invitationProjectState.projectGuests.guestList.find((guest: Guest) => guest.contactId === tempId);
  if (!existingGuest) {
    return;
  }

  // Proceed with updates
  dispatch(updateContact({ tempId, updatedContact }));
  dispatch(updateGuest({ ...updatedGuest, contactId: updatedContact.contactId }));
});

// Define the new reducer
const updateGuestSentStatusesReducer = (state: InvitationState, action: PayloadAction<{ guestIds: number[] }>) => {
    const { guestIds } = action.payload;
    state.invitationProjectState.projectGuests.guestList = state.invitationProjectState.projectGuests.guestList.map(guest => {
        if (guestIds.includes(guest.guestId)) {
            // Mark as sent and clear envelope regeneration flag (envelope was just generated)
            return { ...guest, sentStatus: 'Sent', savedToDb: true, needsEnvelopeRegeneration: false };
        }
        return guest;
    });
    // Optionally disable save button again after status update?
    // state.isSaveEnabled = false; 
};

// 🔥 FIX: New reducer that uses actual send results instead of marking all as 'Sent'
const updateGuestSentStatusesWithResultsReducer = (
    state: InvitationState, 
    action: PayloadAction<{ successfulGuestIds: number[], failedGuestIds: number[] }>
) => {
    const { successfulGuestIds, failedGuestIds } = action.payload;
    state.invitationProjectState.projectGuests.guestList = state.invitationProjectState.projectGuests.guestList.map(guest => {
        if (successfulGuestIds.includes(guest.guestId)) {
            // Mark as sent and clear envelope regeneration flag
            return { ...guest, sentStatus: 'Sent', savedToDb: true, needsEnvelopeRegeneration: false };
        } else if (failedGuestIds.includes(guest.guestId)) {
            // Mark as failed - the actual failure reason is stored in database
            // We use a generic 'Failed' status here; the specific reason will be shown
            // when the guest list is refetched from database
            return { ...guest, sentStatus: 'Failed', savedToDb: true, needsEnvelopeRegeneration: false };
        }
        return guest;
    });
};

// Updated Reducers and Actions
const invitationSlice = createSlice({
  name: 'invitation',
  initialState,
  reducers: {
    setInvitationProjects(state, action: PayloadAction<any[]>) {
      state.invitationProjects = action.payload;
    },
    setInvitationProjectState(state, action: PayloadAction<Partial<EditableInvitationProjectState>>) {
      const timestamp = new Date().toLocaleTimeString();

      state.invitationProjectState = {
        ...state.invitationProjectState,
        ...action.payload,
        projectDetails: {
          ...state.invitationProjectState.projectDetails,
          ...(action.payload.projectDetails ?? {}),
        },
        projectGuests: {
          ...state.invitationProjectState.projectGuests,
          ...(action.payload.projectGuests ?? {}),
          guestList: action.payload.projectGuests?.guestList ?? state.invitationProjectState.projectGuests.guestList,
        },
        design: action.payload.design ? {
          ...state.invitationProjectState.design,
          ...action.payload.design,
          envelopeData: action.payload.design.envelopeData ? {
            ...state.invitationProjectState.design.envelopeData,
            ...action.payload.design.envelopeData,
            textFormatting: action.payload.design.envelopeData.textFormatting ? {
              ...state.invitationProjectState.design.envelopeData.textFormatting,
              ...action.payload.design.envelopeData.textFormatting,
              fontDesign: action.payload.design.envelopeData.textFormatting.fontDesign ? {
                ...state.invitationProjectState.design.envelopeData.textFormatting.fontDesign,
                ...action.payload.design.envelopeData.textFormatting.fontDesign,
              } : state.invitationProjectState.design.envelopeData.textFormatting.fontDesign,
            } : state.invitationProjectState.design.envelopeData.textFormatting,
          } : state.invitationProjectState.design.envelopeData,
          backgroundData: action.payload.design.backgroundData ? {
            ...state.invitationProjectState.design.backgroundData,
            ...action.payload.design.backgroundData,
          } : state.invitationProjectState.design.backgroundData,
          designTextblocks: action.payload.design.designTextblocks ?? state.invitationProjectState.design.designTextblocks,
          imageBlocks: action.payload.design.imageBlocks ?? state.invitationProjectState.design.imageBlocks,
          figureBlocks: action.payload.design.figureBlocks ?? state.invitationProjectState.design.figureBlocks ?? [],
          removedTextblockIds: action.payload.design.removedTextblockIds ?? state.invitationProjectState.design.removedTextblockIds,
          removedImageblockIds: action.payload.design.removedImageblockIds ?? state.invitationProjectState.design.removedImageblockIds,
          removedFigureblockIds: action.payload.design.removedFigureblockIds ?? state.invitationProjectState.design.removedFigureblockIds ?? [],
        } : state.invitationProjectState.design,
        completedSteps: action.payload.completedSteps ?? state.invitationProjectState.completedSteps,
      };
      // 🔧 REMOVED: state.completedSteps sync (only invitationProjectState.completedSteps exists now)
    },
    resetInvitationState(state) {
      // Return initialState correctly
      Object.assign(state, initialState);
      // Ensure nested arrays/objects are reset too if Object.assign is shallow
      state.invitationProjectState = initialState.invitationProjectState; 
      // 🔧 REMOVED: state.completedSteps sync (only invitationProjectState.completedSteps exists now)
    },
    // ============================================================================
    // 🛡️ PROTECTED: Guest list modification reducers
    // ============================================================================
    // These reducers update the guest list IMMEDIATELY in Redux state.
    // The UI should reflect changes instantly without requiring a page refresh.
    //
    // CRITICAL: useProjectGuests hook must NOT have existingGuestList in its
    // useEffect dependencies, otherwise stale fetch data will overwrite these
    // local changes. See useProjectGuests.ts for detailed explanation.
    //
    // HISTORY: Bug discovered Jan 2026 - add/delete not reflecting in UI.
    // ============================================================================
    addSelectedGuest(state, action: PayloadAction<Guest>) {
      const guestPayload = action.payload;

      // Prevent duplicate guests by checking both guestId (if > 0) and contactId
      const isDuplicate = state.invitationProjectState.projectGuests.guestList.some((existingGuest) => {
        if (guestPayload.guestId > 0 && existingGuest.guestId > 0) {
          return existingGuest.guestId === guestPayload.guestId;
        }
        return existingGuest.contactId === guestPayload.contactId;
      });

      if (!isDuplicate) {
        state.invitationProjectState.projectGuests.guestList.push(guestPayload);
      } else {
        console.warn('🚫 Prevented duplicate guest from being added to Redux', {
          guestId: guestPayload.guestId,
          contactId: guestPayload.contactId
        });
      }
    },

    removeSelectedGuest(state, action: PayloadAction<number>) {
      const contactIdToRemove = action.payload;
      
      // Remove from guest list immediately (backend deletion already completed)
      state.invitationProjectState.projectGuests.guestList = state.invitationProjectState.projectGuests.guestList.filter(
        (guest) => guest.contactId !== contactIdToRemove
      );
    },
    // ============================================================================
    // 🛡️ END PROTECTED: Guest list modification reducers
    // ============================================================================
    setNewGuest(state, action: PayloadAction<Partial<NewGuestContact>>) {

      state.invitationProjectState.projectGuests.newGuest = {
        ...state.invitationProjectState.projectGuests.newGuest,
        ...action.payload,
      } as NewGuestContact;

    },
    resetNewGuest(state) {
      state.invitationProjectState.projectGuests.newGuest = {
        contactId: 0,
        guestId: 0,
        invitationProjectId: 0,
        rsvpStatus: '',
        comments: '',
        sentStatus: 'Not Sent',
        tags: [],
        relatives: [],
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        guestStatus: 'publish', // Default to publish
        deletionReason: undefined,
        deletedAt: undefined,
        deletedBy: undefined,
        deletionEmailSent: undefined,
        openedStatus: false, // Default to not opened
        openedCount: 0, // Default to 0 opens
        firstOpenedAt: undefined,
        lastOpenedAt: undefined,
        reminderSentAt: undefined, // Default to no reminder sent
      } as NewGuestContact;
    },
    updateGuest(state, action: PayloadAction<Guest>) {
      const payloadGuest = action.payload;

      // Create a mutable copy of payloadGuest
      const updatedGuest = { ...payloadGuest };

      // Attempt to locate the guest using guestId (primary), contactId, or tempId
      // guestId is the most reliable identifier, especially when switching contacts
      const index = state.invitationProjectState.projectGuests.guestList.findIndex(
        (guest) => 
          (updatedGuest.guestId && guest.guestId === updatedGuest.guestId) || 
          guest.contactId === updatedGuest.contactId || 
          guest.contactId < 0
      );

      if (index !== -1) {
        state.invitationProjectState.projectGuests.guestList[index] = {
          ...state.invitationProjectState.projectGuests.guestList[index],
          ...updatedGuest,
        };
      }
    },
    setLastSavedData(state, action: PayloadAction<CompleteProjectDataType | undefined>) {
      const timestamp = new Date().toLocaleTimeString();
      state.lastSavedData = action.payload;
    },
    setIsSaveEnabled(state, action: PayloadAction<boolean>) {
      // Only update if the value is actually different
      if (state.isSaveEnabled !== action.payload) {
          state.isSaveEnabled = action.payload;
      }
    },
    setEnvelopeFrontInvalidated(state, action: PayloadAction<boolean>) {
      // Set envelope flag in project state for database persistence
      state.invitationProjectState.envelopeFrontInvalidated = action.payload;
    },
    updateProjectSentDateTime(state, action: PayloadAction<{ sentDateTime: string }>) {
      const { sentDateTime } = action.payload;

      // Update ONLY the `sentDateTime` at the project level
      if (state.invitationProjectState.projectId) {
        state.invitationProjectState.projectDetails = {
          ...state.invitationProjectState.projectDetails,
        };
      }
    },
    setLastVisitedPage(state, action: PayloadAction<string>) {
      if (isValidFlowPage(action.payload)) {
        state.invitationProjectState.lastVisitedPage = action.payload;
      }
    },
    markStepCompleted(state, action: PayloadAction<string>) {
        const stepPath = action.payload;
        if (!state.invitationProjectState.completedSteps.includes(stepPath)) {
            state.invitationProjectState.completedSteps = [
                ...state.invitationProjectState.completedSteps,
                 stepPath
            ];
            // 🔧 REMOVED: state.completedSteps sync (only invitationProjectState.completedSteps exists now)
            // Optionally set isSaveEnabled = true here if marking complete should trigger save?
            // state.isSaveEnabled = true;
        }
    },
    updateProjectDesignState(state, action: PayloadAction<Partial<DesignState> & { _skipSaveEnabled?: boolean }>) {
        const { _skipSaveEnabled, ...designPayload } = action.payload;
        state.invitationProjectState.design = {
            ...state.invitationProjectState.design,
            ...designPayload,
        };
        // Enable save button when user makes actual content changes
        // (but not for UI-only changes like activeEditor)
        // Skip if _skipSaveEnabled flag is set (for initialization cases)
        if (!_skipSaveEnabled) {
            const contentChangeKeys = ['designTextblocks', 'imageBlocks', 'figureBlocks', 'envelopeData', 'backgroundData'];
            const hasContentChange = Object.keys(designPayload).some(key => contentChangeKeys.includes(key));
            if (hasContentChange) {
                state.isSaveEnabled = true;
            }
        }
    },
    // --- UPDATED Reducer for Envelope URLs (Explicit Return) --- 
    setInvitationEnvelopeUrls(state, action: PayloadAction<{ envelopeImageUrl?: string; envelopeImageUrlSmall?: string }>) {
        // Ensure the nested structure exists before trying to update
        if (state.invitationProjectState.design && state.invitationProjectState.design.envelopeData) {
            // Return a new envelopeData object with updated URLs
            state.invitationProjectState.design.envelopeData = {
                ...state.invitationProjectState.design.envelopeData, // Spread existing data
                envelopeImageUrl: action.payload.envelopeImageUrl,   // Set new URL
                envelopeImageUrlSmall: action.payload.envelopeImageUrlSmall // Set new URL
            };
            // Also update lastSavedData so this doesn't appear as a "change" for save detection
            // These URLs are background-generated and persisted separately to DB
            if (state.lastSavedData?.design?.envelopeData) {
                state.lastSavedData.design.envelopeData.envelopeImageUrl = action.payload.envelopeImageUrl;
                state.lastSavedData.design.envelopeData.envelopeImageUrlSmall = action.payload.envelopeImageUrlSmall;
            }
        } 
    },
    // Store live preview URL from konvolut for cache reuse on baggrund
    // NOTE: Don't clear envelopeImageUrl here - that would create a "change" vs lastSavedData
    // and trigger isSaveEnabled. The priority logic in baggrund handles stale URLs correctly.
    setEnvelopeLivePreviewUrl(state, action: PayloadAction<{ livePreviewUrl: string; showCard: boolean; livePreviewUrlNoCard?: string }>) {
        if (state.invitationProjectState.design?.envelopeData) {
            state.invitationProjectState.design.envelopeData.livePreviewUrl = action.payload.livePreviewUrl;
            state.invitationProjectState.design.envelopeData.livePreviewShowCard = action.payload.showCard;
            // Also store the showCard=false version for baggrund (prefetched)
            if (action.payload.livePreviewUrlNoCard) {
                state.invitationProjectState.design.envelopeData.livePreviewUrlNoCard = action.payload.livePreviewUrlNoCard;
            }
        }
    },
    // --- END NEW Reducer ---
    
     // Existing reducer for partial URLs (This seems correctly targeted already)
     setEnvelopePartialUrls: (
        state,
        action: PayloadAction<{
          backBottomBehindPartialUrl: string;
          backBottomInfrontPartialUrl: string;
          backTopOpenPartialUrl: string;
          backTopClosedPartialUrl: string;
          frontPartialUrl?: string;
        }>
    ) => {
      if (state.invitationProjectState.design?.envelopeData) { // Ensure target exists
          state.invitationProjectState.design.envelopeData.backBottomBehindPartialUrl = action.payload.backBottomBehindPartialUrl;
          state.invitationProjectState.design.envelopeData.backBottomInfrontPartialUrl = action.payload.backBottomInfrontPartialUrl;
          state.invitationProjectState.design.envelopeData.backTopOpenPartialUrl = action.payload.backTopOpenPartialUrl;
          state.invitationProjectState.design.envelopeData.backTopClosedPartialUrl = action.payload.backTopClosedPartialUrl;
          if (action.payload.frontPartialUrl !== undefined) {
            state.invitationProjectState.design.envelopeData.frontPartialUrl = action.payload.frontPartialUrl;
          }
          // Also update lastSavedData so this doesn't appear as a "change" for save detection
          // These URLs are background-generated and persisted separately to DB
          if (state.lastSavedData?.design?.envelopeData) {
            state.lastSavedData.design.envelopeData.backBottomBehindPartialUrl = action.payload.backBottomBehindPartialUrl;
            state.lastSavedData.design.envelopeData.backBottomInfrontPartialUrl = action.payload.backBottomInfrontPartialUrl;
            state.lastSavedData.design.envelopeData.backTopOpenPartialUrl = action.payload.backTopOpenPartialUrl;
            state.lastSavedData.design.envelopeData.backTopClosedPartialUrl = action.payload.backTopClosedPartialUrl;
            if (action.payload.frontPartialUrl !== undefined) {
              state.lastSavedData.design.envelopeData.frontPartialUrl = action.payload.frontPartialUrl;
            }
          }
      } else {
           console.warn('state.invitationProjectState.design.envelopeData not found!');
      }
    },
    // --- NEW Wishlist Reducers ---
    toggleWishlistEnabled(state, action: PayloadAction<boolean>) {
        state.invitationProjectState.projectDetails.wishlistEnabled = action.payload;
        state.isSaveEnabled = true; // Changing this requires save
    },
    setWishlistType(state, action: PayloadAction<'link' | 'custom'>) {
        state.invitationProjectState.projectDetails.wishlistType = action.payload;
        state.isSaveEnabled = true;
    },
    // New reducers for managing wishlist array
    addWishlist(state, action: PayloadAction<WishlistItem>) {
        if (!state.invitationProjectState.projectDetails.wishlist) {
            state.invitationProjectState.projectDetails.wishlist = [];
        }
        state.invitationProjectState.projectDetails.wishlist.push(action.payload);
        state.isSaveEnabled = true;
    },
    updateWishlist(state, action: PayloadAction<{ index: number; wishlist: WishlistItem }>) {
        const { index, wishlist } = action.payload;
        if (state.invitationProjectState.projectDetails.wishlist && 
            index >= 0 && 
            index < state.invitationProjectState.projectDetails.wishlist.length) {
            state.invitationProjectState.projectDetails.wishlist[index] = wishlist;
            state.isSaveEnabled = true;
        }
    },
    removeWishlist(state, action: PayloadAction<number>) {
        const indexToRemove = action.payload;
        if (state.invitationProjectState.projectDetails.wishlist && 
            indexToRemove >= 0 && 
            indexToRemove < state.invitationProjectState.projectDetails.wishlist.length) {
            state.invitationProjectState.projectDetails.wishlist.splice(indexToRemove, 1);
            state.isSaveEnabled = true;
        }
    },
    addCustomWish(state, action: PayloadAction<{ name: string; url?: string }>) {
        const newWish: CustomWish = {
            id: Math.random().toString(36).substring(2, 15), // Simple unique enough ID
            name: action.payload.name,
            url: action.payload.url || undefined // Store undefined if empty
        };
        if (!state.invitationProjectState.projectDetails.customWishes) {
            state.invitationProjectState.projectDetails.customWishes = [];
        }
        state.invitationProjectState.projectDetails.customWishes.push(newWish);
        state.isSaveEnabled = true; // Adding requires save
    },
    removeCustomWish(state, action: PayloadAction<string>) {
        const idToRemove = action.payload;
        if (state.invitationProjectState.projectDetails.customWishes) {
            state.invitationProjectState.projectDetails.customWishes = 
                state.invitationProjectState.projectDetails.customWishes.filter(wish => wish.id !== idToRemove);
            state.isSaveEnabled = true; // Removing requires save
        }
    },
    // --- Add the new reducer --- 
    updateGuestSentStatuses: updateGuestSentStatusesReducer,
    updateGuestSentStatusesWithResults: updateGuestSentStatusesWithResultsReducer,
    // --- 
    // --- NEW: Reducer to explicitly set project status ---
    setProjectStatus(state, action: PayloadAction<string>) {
      if (state.invitationProjectState.status !== action.payload) {
        state.invitationProjectState.status = action.payload;
      } 
    },
    // --- End NEW Reducer ---
    
    // --- NEW: Actions for storing original design colors ---
    setOriginalDesignColors(state, action: PayloadAction<{
      originalDesignEnvelopeColor?: string | null;
      originalDesignLiningColor?: string | null;
      originalDesignLiningBitmapId?: number | null;
      originalTextFormatting?: any | null;
    }>) {
      if (state.invitationProjectState.design?.envelopeData) {
        const { originalDesignEnvelopeColor, originalDesignLiningColor, originalDesignLiningBitmapId, originalTextFormatting } = action.payload;
        
        if (originalDesignEnvelopeColor !== undefined) {
          state.invitationProjectState.design.envelopeData.originalDesignEnvelopeColor = originalDesignEnvelopeColor;
        }
        if (originalDesignLiningColor !== undefined) {
          state.invitationProjectState.design.envelopeData.originalDesignLiningColor = originalDesignLiningColor;
        }
        if (originalDesignLiningBitmapId !== undefined) {
          state.invitationProjectState.design.envelopeData.originalDesignLiningBitmapId = originalDesignLiningBitmapId;
        }
        if (originalTextFormatting !== undefined) {
          state.invitationProjectState.design.envelopeData.originalTextFormatting = originalTextFormatting;
        }
      }
    },
    
    // --- NEW: Clear Guest Envelope Bitmaps ---
    clearGuestEnvelopeBitmaps(state, action: PayloadAction<{ projectId: number }>) {
      const { projectId } = action.payload;
      
      // Clear envelopeFrontBitmap for all guests in the current project
      if (state.invitationProjectState.projectId === projectId) {
        state.invitationProjectState.projectGuests.guestList = state.invitationProjectState.projectGuests.guestList.map(guest => ({
          ...guest,
          envelopeFrontBitmap: undefined // Clear the envelope bitmap
        }));
      }
    },
    
    // --- NEW: Clear Deleted Guest IDs After Successful Save ---
    // --- End NEW Actions ---
    
    // --- Mobile Text Edit Panel ---
    setMobileTextEditPanel(state, action: PayloadAction<{ open: boolean; activeTextBlockId: string | null }>) {
      state.mobileTextEditPanel = action.payload;
    },
    setMobileFigureEditPanel(state, action: PayloadAction<{ open: boolean; activeFigureBlockId: string | null }>) {
      state.mobileFigureEditPanel = action.payload;
    },
  },
});

// Export Actions and Reducer
export const {
  setInvitationProjects,
  setInvitationProjectState,
  resetInvitationState,
  updateProjectDesignState, // Export the new action
  addSelectedGuest,
  removeSelectedGuest,
  setNewGuest,
  resetNewGuest,
  updateGuest,
  setLastSavedData,
  setIsSaveEnabled,
  setEnvelopeFrontInvalidated,
  updateProjectSentDateTime,
  setLastVisitedPage,
  markStepCompleted,
  setInvitationEnvelopeUrls, // <<< EXPORT new action
  setEnvelopeLivePreviewUrl, // Store live preview URL from konvolut for cache reuse
  setEnvelopePartialUrls, // Ensure this was exported if it wasn't already
  toggleWishlistEnabled,
  setWishlistType,
  addWishlist,
  updateWishlist,
  removeWishlist,
  addCustomWish,
  removeCustomWish,
  updateGuestSentStatuses, // <-- Export the new action creator
  updateGuestSentStatusesWithResults, // 🔥 FIX: Export new action that uses actual send results
  setProjectStatus, // <-- Export the new action creator
  setOriginalDesignColors, // <-- Export the new action creator for original design colors
  clearGuestEnvelopeBitmaps, // <-- Export the new action creator for clearing envelope bitmaps
  setMobileTextEditPanel,
  setMobileFigureEditPanel,
} = invitationSlice.actions;

export default invitationSlice.reducer;